/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Button, Popover } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

interface IAccountCustomerBalanceActions {
    className?: string;
    requestStatementButtonLabel: string;
    onRequestStatement(): void;

}

interface IAdminRequestStatementProps {
    className?: string;
    requestStatementButtonLabel: string;
    myUserAccountLabel: string;
    fullOrganizationLabel: string;
    isDropdownShowing: boolean;
    buttonReference?: React.RefObject<HTMLButtonElement>;
    onToggleDropdown(): void;
    onRequestStatement(isFullOrg: boolean): void;
}

export const ActionBar: React.FC<IAccountCustomerBalanceActions> = ({ className, onRequestStatement, requestStatementButtonLabel }) => {
    return (
        <div className={className}>
            <Button className={`${className}__action__requestStatement`} onClick={onRequestStatement}>
                {requestStatementButtonLabel}
            </Button>
        </div>
    );
};

export const AdminRequestStatementButton: React.FC<IAdminRequestStatementProps> = (
    { className, onRequestStatement, requestStatementButtonLabel,
        onToggleDropdown, myUserAccountLabel, fullOrganizationLabel,
        isDropdownShowing, buttonReference }) => {

    const onSelectCustomerAccount = React.useCallback(() => {
        onRequestStatement(false);
    }, [onRequestStatement]);

    const onSelectFullOrg = React.useCallback(() => {
        onRequestStatement(true);
    }, [onRequestStatement]);

    const popoverReference = buttonReference !== undefined ? buttonReference : React.createRef<HTMLButtonElement>();

    return (
        <div className={`${className}__dropdown`}>
            <Button
                className={isDropdownShowing ? `${className}__action__requestStatement__toggleUp` : `${className}__action__requestStatement__toggleDown`}
                onClick={onToggleDropdown}
                innerRef={popoverReference}
                aria-haspopup='true'
                aria-expanded={isDropdownShowing}
            >
                <span className={`${className}__action__requestStatement__buttonLabel`}>
                    {requestStatementButtonLabel}
                </span>
            </Button>
            <Popover
                className={`${className}__action__requestStatement__dropdownVisible`}
                isOpen={isDropdownShowing}
                placement='bottom'
                target={popoverReference}
                toggle={onToggleDropdown}
                role='listbox'
                aria-hidden={!isDropdownShowing}
            >
                <Button
                    onClick={onSelectCustomerAccount}
                    className={`${className}__action__requestStatement__dropdownOption`}
                    role='option'
                >
                    {myUserAccountLabel}
                </Button>
                <Button
                    onClick={onSelectFullOrg}
                    className={`${className}__action__requestStatement__dropdownOption`}
                    role='option'
                >
                    {fullOrganizationLabel}
                </Button>
            </Popover>
        </div>
    );
};
